export const leadSourceValues = {
  MANUAL: 'Manual',
  LEAD_FINDER: 'Lead Finder',
  OLX: 'OLX',
  CARWALE: 'CarWale',
  GAADI: 'Gaadi',
  CARDEKHO: 'CarDekho',
  GMAIL: 'Gmail',
  GOOGLE_MAPS: 'Google Maps',
  FACEBOOK: 'Facebook',
  INSTAGRAM: 'Instagram',
  THREADS: 'Threads',
  WHATSAPP: 'WhatsApp',
  WHATSAPP_FLOW: 'WhatsApp Flow',
} as const;

export type LeadSourceValue = (typeof leadSourceValues)[keyof typeof leadSourceValues];

export const leadSourceOptions: LeadSourceValue[] = Object.values(leadSourceValues).sort(
  (left, right) => left.localeCompare(right)
);

export const coreLeadSourceKeys = ['OLX', 'CARDEKHO', 'CARWALE', 'WHATSAPP'] as const;

export const configuredLeadSourceKeys = [
  ...coreLeadSourceKeys,
  'MANUAL',
  'GMAIL',
  'GOOGLE_MAPS',
  'FACEBOOK',
  'INSTAGRAM',
  'THREADS',
  'UNKNOWN',
] as const;

export type CoreLeadSourceKey = (typeof coreLeadSourceKeys)[number];
export type ConfiguredLeadSourceKey = (typeof configuredLeadSourceKeys)[number];

export type NormalizedLeadSource = {
  key: string;
  label: string;
  isCore: boolean;
  isConfigured: boolean;
  isUnknown: boolean;
};

const configuredLeadSourceLabels: Record<ConfiguredLeadSourceKey, string> = {
  OLX: leadSourceValues.OLX,
  CARDEKHO: leadSourceValues.CARDEKHO,
  CARWALE: leadSourceValues.CARWALE,
  WHATSAPP: leadSourceValues.WHATSAPP,
  MANUAL: leadSourceValues.MANUAL,
  GMAIL: leadSourceValues.GMAIL,
  GOOGLE_MAPS: leadSourceValues.GOOGLE_MAPS,
  FACEBOOK: leadSourceValues.FACEBOOK,
  INSTAGRAM: leadSourceValues.INSTAGRAM,
  THREADS: leadSourceValues.THREADS,
  UNKNOWN: 'Unknown',
};

function cleanSource(value: string) {
  return value.trim().replace(/[_-]+/g, ' ').replace(/\s+/g, ' ');
}

function sourceMatchKey(value: string) {
  return cleanSource(value)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '');
}

function toDisplayLabel(value: string) {
  const cleaned = cleanSource(value);

  if (!cleaned) {
    return configuredLeadSourceLabels.UNKNOWN;
  }

  return cleaned
    .split(' ')
    .map(part => (part ? `${part[0].toUpperCase()}${part.slice(1).toLowerCase()}` : part))
    .join(' ');
}

export function getLeadSourceLabel(sourceKey: string) {
  if (sourceKey in configuredLeadSourceLabels) {
    return configuredLeadSourceLabels[sourceKey as ConfiguredLeadSourceKey];
  }

  if (sourceKey.startsWith('CUSTOM:')) {
    return sourceKey.slice('CUSTOM:'.length);
  }

  return sourceKey || configuredLeadSourceLabels.UNKNOWN;
}

export function normalizeLeadSource(rawSource?: string | null): NormalizedLeadSource {
  const raw = cleanSource(rawSource ?? '');

  if (!raw) {
    return {
      key: 'UNKNOWN',
      label: configuredLeadSourceLabels.UNKNOWN,
      isCore: false,
      isConfigured: true,
      isUnknown: true,
    };
  }

  const matchKey = sourceMatchKey(raw);
  let key: ConfiguredLeadSourceKey | null = null;

  if (matchKey === 'unknown') {
    key = 'UNKNOWN';
  } else if (matchKey === 'olx' || matchKey.includes('olx')) {
    key = 'OLX';
  } else if (
    ['cardekho', 'cardekhocom', 'gaadi'].includes(matchKey) ||
    matchKey.includes('cardekho')
  ) {
    key = 'CARDEKHO';
  } else if (['carwale', 'carwalecom'].includes(matchKey) || matchKey.includes('carwale')) {
    key = 'CARWALE';
  } else if (
    ['whatsapp', 'wa', 'whatsappinbound', 'whatsappbusiness'].includes(matchKey) ||
    matchKey.includes('whatsapp')
  ) {
    key = 'WHATSAPP';
  } else if (matchKey === 'manual' || matchKey === 'user' || matchKey === 'useraction') {
    key = 'MANUAL';
  } else if (matchKey === 'gmail' || matchKey === 'googlemail' || matchKey.includes('gmail')) {
    key = 'GMAIL';
  } else if (matchKey === 'facebook') {
    key = 'FACEBOOK';
  } else if (matchKey === 'instagram') {
    key = 'INSTAGRAM';
  } else if (matchKey === 'threads') {
    key = 'THREADS';
  } else if (
    matchKey === 'leadfinder' ||
    matchKey === 'googlemaps' ||
    matchKey === 'googleplaces' ||
    matchKey.includes('googlemaps') ||
    matchKey.includes('googleplaces')
  ) {
    key = 'GOOGLE_MAPS';
  }

  if (key) {
    return {
      key,
      label: configuredLeadSourceLabels[key],
      isCore: coreLeadSourceKeys.includes(key as CoreLeadSourceKey),
      isConfigured: true,
      isUnknown: key === 'UNKNOWN',
    };
  }

  const label = toDisplayLabel(raw);
  return {
    key: `CUSTOM:${label}`,
    label,
    isCore: false,
    isConfigured: false,
    isUnknown: false,
  };
}

export function getConfiguredLeadSources() {
  const sources = new Map<string, NormalizedLeadSource>();
  for (const value of [...leadSourceOptions, configuredLeadSourceLabels.UNKNOWN]) {
    const source = normalizeLeadSource(value);
    sources.set(source.key, source);
  }
  return Array.from(sources.values());
}
